import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { MessageService } from 'primeng/api';
import { AccessRole, AuthenticationRequest, AuthService, LoadingPanelService, LocalService, noWhitespaceValidator } from 'lib-base';
import {firstValueFrom, forkJoin, Subscription} from 'rxjs';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';
import {IconFieldModule} from "primeng/iconfield";
import {InputIconModule} from "primeng/inputicon";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RecaptchaV3Module,
    ButtonModule, InputTextModule, PasswordModule, IconFieldModule, InputIconModule
  ],
  templateUrl: "./login.component.html",
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy {

  loginForm!: FormGroup
  recaptchaSubscription !: Subscription;
  currentTime !: Date;
  location!: string;
  translations: any = {};

  constructor(private readonly _formBuilder: FormBuilder, private readonly _router: Router,
    private readonly _authService: AuthService, private readonly _lps: LoadingPanelService, private _t: TranslateService,
    private readonly _messageService: MessageService, private readonly recaptchaV3Service: ReCaptchaV3Service,
    private readonly _ls: LocalService
  ) {
  }

  ngOnInit(): void {
    this.loginForm = this._formBuilder.group({
      correo: [null, [Validators.required, noWhitespaceValidator]],
      password: [null, [Validators.required, noWhitespaceValidator]]
    });
    this.translate();
    this.updateLocation();
    this.updateTime();
  }

  ngOnDestroy(): void {
    if (this.recaptchaSubscription) {
      this.recaptchaSubscription.unsubscribe();
    }
  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      exito: this._t.get('common.exito'),
      errorSummary: this._t.get('form.msg.error-summary'),
      errorDetail: this._t.get('components.app-login.usuario-pass-incorrecto'),
      usuario: this._t.get('components.app-login.correo'),
      password: this._t.get('components.app-login.password'),
      btnIniciarSesion: this._t.get('components.app-login.btn-iniciar-sesion'),
      recuperarPsw: this._t.get('components.app-login.olvido-contrasena'),
      message: this._t.get('components.app-login.subtitle'),
      welcome: this._t.get('common.bienvenido'),
    }));
  }

  onSubmit() {
    if (this.loginForm.invalid) return;

    this.recaptchaSubscription = this.recaptchaV3Service.execute('administracionlogin')
      .subscribe((token) => {
        const request = {
          correo: this.loginForm.get('correo')?.value,
          password: this.loginForm.get('password')?.value,
          captchaToken: token
        } as AuthenticationRequest;

        this.authenticate(request);
      });
  }

  recuperarContrasena() {
    this._router.navigate(['public/solicitud-restablecer-password']);
  }

  authenticate(request: AuthenticationRequest) {
    this._lps.show();
    this._authService.authenticate(request).subscribe({
      next: (response) => {
        this._lps.hide();
        if (this._ls.hasRole(AccessRole.GESTION_TRAMITE)) {
          this._router.navigate(["configuracion/landing"]);
        } else if (this._ls.hasRole(AccessRole.ADMIN_TRAM)) {
          this._router.navigate(["configuracion/gestion-tramites"]);
        } else if (this._ls.hasRole(AccessRole.GESTION_PQRSD)) {
          this._router.navigate(["configuracion/mis-pqrsd"]);
        }else if(this._ls.hasRole(AccessRole.ADMIN_PQRSD)){
          this._router.navigate(["configuracion/gestion-pqrsd"]);
        }
      },
      error: (error) => {
        this._lps.hide();
        this._messageService.add({ severity: 'error',
          summary:this.translations.errorSummary,
          detail: this.translations.errorDetail });
      }
    });
  }

  updateTime(): void {
    this.currentTime = new Date();
  }

  updateLocation(): void {
    const now = new Date();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = -now.getTimezoneOffset() / 60;
    const offsetString = offset >= 0 ? `+${offset}:00` : `${offset}:00`;
    this.location = `${timeZone} ${offsetString}`;
  }
}
